import Dexie, { type Table } from 'dexie';
import type { EncryptedBlob } from '../crypto/aesgcm';

export interface TaxpayerRow {
  /** HMAC-SHA256(TIN) truncated to 128 bits, base64url — opaque network identifier. */
  token: string;
  encryptedName: EncryptedBlob;
  encryptedAddress: EncryptedBlob;
  encryptedTin: EncryptedBlob;
  createdAt: number;
}

export interface TransactionRow {
  /** Random ULID/UUID; not derived from any PII. */
  id: string;
  /** References TaxpayerRow.token. */
  taxpayerToken: string;
  encryptedAmount: EncryptedBlob;
  encryptedDescription: EncryptedBlob;
  createdAt: number;
}

export type VaultMetaId = 'singleton';

export interface VaultMetaRow {
  id: VaultMetaId;
  kdfAlgorithm: 'PBKDF2-SHA256';
  kdfSalt: string;
  kdfIterations: number;
  /** HMAC(verifierKey, "cruzfind.verifier.v1") — checked on unlock to detect wrong password. */
  verifier: string;
  /** Display email, encrypted under the user's AES key. Optional — populated by AuthService. */
  encryptedEmail?: EncryptedBlob;
  createdAt: number;
}

export interface SerialCounterRow {
  /** Composite id: `${branchCode}/${terminalId}`. */
  id: string;
  branchCode: string;
  terminalId: string;
  lastCounter: number;
  updatedAt: number;
}

export type SubscriberProfileId = 'singleton';
export type EntityClass = 'DTI' | 'SEC';

export interface SubscriberProfileRow {
  id: SubscriberProfileId;
  encryptedBusinessName: EncryptedBlob;
  encryptedBusinessAddress: EncryptedBlob;
  isRegistered: boolean;
  encryptedBusinessTin?: EncryptedBlob;
  /** Plaintext entity class — DTI for sole proprietors, SEC for corporations/partnerships. */
  entityClass?: EntityClass;
  encryptedDtiRegistrationNumber?: EncryptedBlob;
  encryptedSecRegistrationNumber?: EncryptedBlob;
  encryptedLogoBase64?: EncryptedBlob;
  /** Statutory lock flag (RMC 77-2024). Plaintext for fast predicate checks. */
  isLocked?: boolean;
  accountExpirationDate?: number;
  updatedAt: number;
}

export type DocumentType = 'SALES_INVOICE' | 'BILLING_STATEMENT';
export type LedgerStatus = 'draft' | 'issued' | 'locked' | 'voided';

export interface InvoiceSequenceRow {
  /** Composite anchor: `${tenantId}::${profileId}`. */
  sequenceKey: string;
  nextInvoiceNumber: number;
  nextBillingNumber: number;
  updatedAt: number;
}

export interface SalesLedgerRow {
  id: string;
  sequenceKey: string;
  serialString: string;
  documentType: DocumentType;
  timestamp: number;
  encryptedPayload: EncryptedBlob;
  status: LedgerStatus;
}

export type ExpenseCategory =
  | 'RENT'
  | 'UTILITIES'
  | 'SALARIES'
  | 'SUPPLIES'
  | 'REPRESENTATION'
  | 'OTHERS';

export interface ExpenseLedgerRow {
  id: string;
  date: number;
  encryptedVendorName: EncryptedBlob;
  encryptedVendorTin?: EncryptedBlob;
  category: ExpenseCategory;
  amountGross: number;
  amountNet: number;
  vatInput: number;
  isVatReceipt: boolean;
  encryptedAttachmentBlob?: EncryptedBlob;
}

export class CruzFindVault extends Dexie {
  taxpayers!: Table<TaxpayerRow, string>;
  transactions!: Table<TransactionRow, string>;
  vaultMeta!: Table<VaultMetaRow, VaultMetaId>;
  serialCounters!: Table<SerialCounterRow, string>;
  subscriber_profile!: Table<SubscriberProfileRow, SubscriberProfileId>;
  invoice_sequences!: Table<InvoiceSequenceRow, string>;
  sales_ledger!: Table<SalesLedgerRow, string>;
  expense_ledger!: Table<ExpenseLedgerRow, string>;

  constructor(dbName = 'CruzFindVault') {
    super(dbName);
    this.version(1).stores({
      taxpayers: 'token, createdAt',
      transactions: 'id, taxpayerToken, createdAt',
      vaultMeta: 'id',
      serialCounters: 'id, branchCode, terminalId',
    });
    this.version(2).stores({
      taxpayers: 'token, createdAt',
      transactions: 'id, taxpayerToken, createdAt',
      vaultMeta: 'id',
      serialCounters: 'id, branchCode, terminalId',
      subscriber_profile: 'id',
    });
    this.version(3).stores({
      taxpayers: 'token, createdAt',
      transactions: 'id, taxpayerToken, createdAt',
      vaultMeta: 'id',
      serialCounters: 'id, branchCode, terminalId',
      subscriber_profile: 'id',
      invoice_sequences: 'sequenceKey',
      sales_ledger: 'id, sequenceKey, timestamp, status, documentType',
    });
    this.version(4).stores({
      taxpayers: 'token, createdAt',
      transactions: 'id, taxpayerToken, createdAt',
      vaultMeta: 'id',
      serialCounters: 'id, branchCode, terminalId',
      subscriber_profile: 'id',
      invoice_sequences: 'sequenceKey',
      sales_ledger: 'id, sequenceKey, timestamp, status, documentType',
      expense_ledger: 'id, date, category, isVatReceipt',
    });
  }
}
